import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { LoaderComponent } from '~shared/components/loader/loader.component';
import { FormErrors, FormErrorsUtils } from '~shared/utils/form-errors.utils';

/** Durée d'affichage (ms) des toasts d'erreur et d'information longue. */
const ERROR_TOAST_TIMEOUT = 15000;

@Injectable({ providedIn: 'root' })
export class PuToastrService {
  /** Séparateur des messages techniques api-data : `clé -- fichier -- trace`. */
  private static readonly TECHNICAL_MESSAGE_SEPARATOR = ' -- ';
  /** Marqueur d'une stack trace PHP embarquée dans un message d'erreur. */
  private static readonly STACK_TRACE_MARKER = 'Stack trace:';
  /** Message d'erreur générique affiché quand rien de lisible n'est dispo. */
  private static readonly DEFAULT_ERROR_I18N_KEY = 'TOASTR.DEFAULT_ERROR';
  /** Délai minimal (ms) laissé au lecteur d'écran pour lire une annonce. */
  private static readonly DELAI_ANNONCE_MIN_MS = 1000;
  /** Temps de lecture ajouté par caractère (ms), au-delà du minimum. */
  private static readonly MS_PAR_CARACTERE = 50;

  private toastr = inject(ToastrService);
  private translateService = inject(TranslateService);
  // Région live persistante du CDK : voir `annonce()`.
  private annonceur = inject(LiveAnnouncer);

  /** File des messages en attente d'annonce (cf. `annonce()`). */
  private readonly fileAnnonces: {
    message: string;
    politesse: 'assertive' | 'polite';
  }[] = [];
  /** Vrai tant qu'une annonce est en cours de lecture. */
  private annonceEnCours = false;
  /** Annonce en cours de lecture (pour pouvoir la préempter sur erreur). */
  private annonceCourante: {
    message: string;
    politesse: 'assertive' | 'polite';
  } | null = null;
  /** Timer de l'annonce en cours (annulé en cas de préemption). */
  private timerEnCours: ReturnType<typeof setTimeout> | null = null;

  /**
   * Affiche un toast ET recopie son message dans la région live du lecteur
   * d'écran.
   *
   * ## Pourquoi recopier le message
   *
   * ngx-toastr pose `role="alert"` sur le message, dans un conteneur
   * `aria-live="polite"`. En pratique ce patron n'est PAS annoncé de façon
   * fiable (signalé en recette) : un `role="alert"` assertif imbriqué dans une
   * région polie a un comportement indéfini, et surtout le toast est inséré en
   * BLOC (gros sous-arbre) dans la région — or un lecteur d'écran n'annonce
   * fiablement qu'un CHANGEMENT DE TEXTE dans une région qui PRÉEXISTE. Un
   * message qui apparaît pendant un changement de focus (erreur de connexion…)
   * passe alors à la trappe.
   *
   * `LiveAnnouncer` (@angular/cdk/a11y), déjà utilisé ailleurs dans l'appli,
   * gère exactement ce patron fiable : une région persistante dont on change le
   * seul texte. On y recopie donc chaque message, sans toucher au rendu visuel.
   *
   * Erreurs = `assertive` (interrompt) ; succès / info / avertissement =
   * `polite` (n'interrompt pas la lecture en cours).
   */
  private emettreSucces(message: string): void {
    this.toastr.success(message);
    this.annonce(message, 'polite');
  }
  private emettreErreur(
    message: string,
    options?: Partial<IndividualConfig>
  ): void {
    this.toastr.error(message, undefined, options);
    this.annonce(message, 'assertive');
  }
  private emettreInfo(
    message: string,
    title?: string,
    options?: Partial<IndividualConfig>
  ): void {
    this.toastr.info(message, title, options);
    this.annonce(message, 'polite');
  }
  private emettreAvert(message: string): void {
    this.toastr.warning(message);
    this.annonce(message, 'polite');
  }
  /**
   * Met le message en FILE plutôt que de l'annoncer tout de suite.
   *
   * La région live du CDK est unique : `announce()` remplace le texte
   * précédent. Deux toasts rapprochés écraseraient donc la première annonce
   * avant que le lecteur d'écran l'ait lue. On les annonce donc l'un APRÈS
   * l'autre, en laissant à chacun un temps de lecture.
   *
   * Les ERREURS sont prioritaires. Une annonce assertive :
   * - PRÉEMPTE une annonce POLIE en cours de lecture : on la coupe et on la
   *   REMET en tête de file pour la rejouer (rien n'est perdu), car une erreur
   *   doit interrompre, pas attendre ;
   * - passe DEVANT les annonces polies déjà en attente ;
   * - ne coupe PAS une autre erreur en cours, et reste FIFO entre erreurs.
   */
  private annonce(message: string, politesse: 'assertive' | 'polite'): void {
    const annonce = { message, politesse };
    if (politesse === 'assertive') {
      // Préempte une annonce polie en cours : coupée puis remise en tête, donc
      // rejouée après l'erreur — jamais perdue.
      if (this.annonceCourante?.politesse === 'polite') {
        if (this.timerEnCours) {
          clearTimeout(this.timerEnCours);
          this.timerEnCours = null;
        }
        this.fileAnnonces.unshift(this.annonceCourante);
        this.annonceCourante = null;
        this.annonceEnCours = false;
      }
      // Passe devant les annonces polies en attente.
      const premierPoli = this.fileAnnonces.findIndex(
        (a) => a.politesse === 'polite'
      );
      if (premierPoli === -1) {
        this.fileAnnonces.push(annonce);
      } else {
        this.fileAnnonces.splice(premierPoli, 0, annonce);
      }
    } else {
      this.fileAnnonces.push(annonce);
    }
    this.pomperFile();
  }

  private pomperFile(): void {
    if (this.annonceEnCours) {
      return;
    }
    const suivant = this.fileAnnonces.shift();
    if (!suivant) {
      return;
    }
    this.annonceEnCours = true;
    this.annonceCourante = suivant;
    this.annonceur.announce(suivant.message, suivant.politesse);
    // Laisse le temps de lire avant de remplacer le texte par le suivant.
    this.timerEnCours = setTimeout(() => {
      this.annonceEnCours = false;
      this.annonceCourante = null;
      this.timerEnCours = null;
      this.pomperFile();
    }, this.delaiLecture(suivant.message));
  }

  private delaiLecture(message: string): number {
    return Math.max(
      PuToastrService.DELAI_ANNONCE_MIN_MS,
      message.length * PuToastrService.MS_PAR_CARACTERE
    );
  }

  showInsertMessage() {
    this.translateService.get('TOASTR').subscribe((toastr: any) => {
      this.emettreSucces(toastr.INSERT);
    });
  }
  showDeleteMessage(number?: number) {
    let label = 'TOASTR.SUCCESS';
    if (number && number > 1) label = 'TOASTR.SUCCESS_MULTIPLE';
    return this.translateService
      .get(label, { number })
      .subscribe((toastr: any) => {
        this.emettreSucces(toastr);
      });
  }

  showUpdateMessage() {
    this.translateService.get('TOASTR').subscribe((toastr: any) => {
      this.emettreSucces(toastr.UPDATE);
    });
  }

  showSendMessage(message: string) {
    this.translateService.get('TOASTR').subscribe((toastr: any) => {
      this.emettreSucces(toastr[message]);
    });
  }

  showPreferenceUpdateMessage() {
    this.translateService.get('TOASTR').subscribe((toastr: any) => {
      this.emettreSucces(toastr.PREFERENCE_UPDATED);
    });
  }

  warning(message: string, limit?: number) {
    this.translateService
      .get(`TOASTR.${message}`, { number: limit })
      .subscribe((toastr: any) => {
        this.emettreAvert(toastr);
      });
  }

  /**
   * Avertissement à partir d'une clé i18n complète (hors namespace TOASTR)
   * avec des paramètres d'interpolation arbitraires.
   */
  warningKey(key: string, params?: Record<string, unknown>) {
    this.translateService.get(key, params).subscribe((message: string) => {
      this.emettreAvert(message);
    });
  }

  /**
   * Succès à partir d'une clé i18n complète (hors namespace TOASTR), annoncé
   * comme les autres succès.
   */
  successKey(key: string, params?: Record<string, unknown>) {
    this.translateService.get(key, params).subscribe((message: string) => {
      this.emettreSucces(message);
    });
  }

  importFieldError(message: string, concernedColumn: string, limit?: number) {
    this.translateService
      .get(`TOASTR.${message}`, { number: limit, column: concernedColumn })
      .subscribe((toastr: any) => {
        this.emettreErreur(toastr, { timeOut: ERROR_TOAST_TIMEOUT });
      });
  }

  info(message: string, title = '') {
    this.translateService.get(`TOASTR.${message}`).subscribe((toastr: any) => {
      this.emettreInfo(toastr, title, { timeOut: ERROR_TOAST_TIMEOUT });
    });
  }

  showDashboardMissingBlocError(): void {
    this.translateService.get('TOASTR').subscribe((toastr: any) => {
      this.emettreErreur(toastr.DASHBOARD_BLOC_NOT_LOADED, {
        timeOut: 5000,
      });
    });
  }

  showDashboardBlocFoundMessage(): void {
    this.translateService.get('TOASTR').subscribe((toastr: any) => {
      this.emettreInfo(toastr.DASHBOARD_BLOC_FOUND, undefined, {
        timeOut: 5000,
      });
    });
  }

  /**
   * Affiche l'erreur renvoyée par l'API. Si la réponse ne porte ni
   * `message` ni `form_errors` exploitables (corps HTML d'un proxy 5xx,
   * erreur réseau, corps vide…), affiche le message générique
   * TOASTR.DEFAULT_ERROR : un échec ne doit jamais être silencieux
   * (cf. ticket 869ehtfbq). Un message technique (clé + stack trace) est
   * réduit à sa clé et traduit avant affichage (cf. ticket 869ehtvet).
   */
  showErrorMessage(error: unknown, loader?: LoaderComponent) {
    const body = (error as { error?: unknown } | null | undefined)?.error;
    const apiError =
      body && typeof body === 'object'
        ? (body as { message?: unknown; form_errors?: FormErrors })
        : undefined;
    const message =
      typeof apiError?.message === 'string' ? apiError.message : undefined;
    const formErrors = apiError?.form_errors;
    // Un `form_errors` sans aucune entrée (`{}`, ou `[]` quand l'API sérialise
    // un tableau PHP vide) reste truthy : s'y fier produirait un toast au
    // message vide tout en bloquant le repli, c'est-à-dire l'échec silencieux
    // que cette méthode doit justement éviter.
    const hasFormErrors = !!formErrors && !FormErrorsUtils.isEmpty(formErrors);

    if (message) {
      this.emettreErreur(this.toDisplayableServerMessage(message), {
        timeOut: ERROR_TOAST_TIMEOUT,
      });
    }
    if (hasFormErrors) {
      let toastMessage = '';
      const errorObject: FormErrors = FormErrorsUtils.translate(
        formErrors,
        this.translateService
      );
      Object.entries(errorObject).forEach(([key, value]) => {
        toastMessage += `${key} : ${value.join(', ')}` + '\n\r';
      });
      this.emettreErreur(toastMessage, {
        timeOut: ERROR_TOAST_TIMEOUT,
      });
    }
    if (!message && !hasFormErrors) {
      this.showFrontErrorMessage('DEFAULT_ERROR');
    }
    loader?.setLoading(false);
  }

  /**
   * Convertit un message d'erreur serveur en texte affichable — aucune trace
   * technique ne doit jamais atteindre un toast :
   * - un message technique (`clé -- fichier:ligne -- Stack trace: #0 …`,
   *   renvoyé par api-data en mode debug) est réduit à son premier segment
   *   (la clé d'erreur) ;
   * - la clé est traduite via le catalogue `API_ERRORS` (même pipeline que
   *   les form_errors, cf. FormErrorsUtils) ;
   * - sans traduction : un message technique retombe sur le générique
   *   TOASTR.DEFAULT_ERROR, un message humain est affiché tel quel.
   */
  private toDisplayableServerMessage(message: unknown): string {
    if (typeof message !== 'string') {
      return this.translateService.instant(
        PuToastrService.DEFAULT_ERROR_I18N_KEY
      );
    }
    const isTechnical =
      message.includes(PuToastrService.TECHNICAL_MESSAGE_SEPARATOR) ||
      message.includes(PuToastrService.STACK_TRACE_MARKER);
    const key = isTechnical
      ? message
          .split(PuToastrService.TECHNICAL_MESSAGE_SEPARATOR)[0]
          .split(PuToastrService.STACK_TRACE_MARKER)[0]
          .trim()
      : message;
    const translated = FormErrorsUtils.translateMessage(
      key,
      this.translateService
    );
    if (translated !== key) {
      return translated;
    }
    return isTechnical
      ? this.translateService.instant(PuToastrService.DEFAULT_ERROR_I18N_KEY)
      : message;
  }

  showFrontErrorMessage(errorKey: string, loader?: LoaderComponent) {
    this.translateService.get('TOASTR').subscribe((toastr: any) => {
      this.emettreErreur(toastr[errorKey], {
        timeOut: ERROR_TOAST_TIMEOUT,
      });
      loader?.setLoading(false);
    });
  }

  showSuccessExportMessage() {
    this.translateService.get('TOASTR').subscribe((toastr: any) => {
      this.emettreSucces(toastr.EXPORT_SUCCESS);
    });
  }
}
